import { EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { AttioDealResponse, AttioTeamMemberResponse, AttioTeamRecord, AttioTeamRecordQueryResponse, AttioWorkspaceMemberResponse } from '../attio.types';
import { BaseAttioService } from './BaseAttioService';

// Service-specific types
export interface AttioEngagementManagerData {
  name: string;
  imageSrc: string;
  targetRecordId: string;
}

export interface AttioValuationEngagementManagerData {
  name: string;
  imageSrc: string;
}

export interface AttioEngagementManagerUpdateParams {
  valuationId: string;
  engagementManagerTargetRecordIds: string[];
}

export class AttioEngagementService extends BaseAttioService {
  constructor() {
    super('AttioEngagementService');
  }
  /**
   * Get engagement manager details directly by engagement manager record ID
   */
  async getEngagementManager(engagementManagerRecordId: string): Promise<AttioEngagementManagerData> {
    try {
      if (engagementManagerRecordId) {
        // Get engagement manager details directly
        const engagementManagerResponse = await this.get<AttioWorkspaceMemberResponse>(
          this.endpoints.retrieveWorkspaceMemberByRecordId(engagementManagerRecordId)
        );

        // Handle nested structure for workspace member data
        const engagementManagerData = engagementManagerResponse.data;

        return {
          name: `${engagementManagerData.first_name} ${engagementManagerData.last_name}`,
          imageSrc: engagementManagerData.avatar_url || '-',
          targetRecordId: engagementManagerRecordId,
        };
      }
      // Default values if no engagement manager ID provided
      return {
        name: '-',
        imageSrc: '-',
      };
    } catch (error) {
      console.warn('Error fetching engagement manager details:', error);
      return {
        name: '-',
        imageSrc: '-',
      };
    }
  }

  /**
   * Get valuation engagement manager details directly by engagement manager target record ID
   */
  async getValuationEngagementManager(engagementManagerRecordId: string): Promise<AttioValuationEngagementManagerData> {
    try {
      if (engagementManagerRecordId) {
        // Get engagement manager details directly
        const engagementManagerResponse = await this.get<AttioTeamMemberResponse>(
          this.endpoints.retrieveTeamByRecordId(engagementManagerRecordId)
        );
  
        // Handle nested structure for workspace member data
        const engagementManagerData = engagementManagerResponse.data.values;
  
        return {
          name: Array.isArray(engagementManagerData?.name) && engagementManagerData.name[0]?.value
            ? engagementManagerData.name[0].value
            : '-',
          imageSrc: Array.isArray(engagementManagerData?.profile_image_url) && engagementManagerData.profile_image_url[0]?.value
            ? engagementManagerData.profile_image_url[0].value
            : '-',
        };
      }
      // Default values if no engagement manager ID provided
      return {
        name: '-',
        imageSrc: '-',
      };
    } catch (error) {
      console.warn('Error fetching engagement manager details:', error);
      return {
        name: '-',
        imageSrc: '-',
      };
    }
  }

  /**
   * Get engagement manager details for a valuation record through deal ID
   */
  async getEngagementManagerByDealId(dealRecordId: string): Promise<AttioEngagementManagerData> {
    try {
      if (dealRecordId) {
        // Get deal details to find engagement manager
        try {
          const dealResponse = await this.get<AttioDealResponse>(this.endpoints.retrieveDealByRecordId(dealRecordId));

          const dealResponseData = dealResponse.data;
          const { values: dealData } = dealResponseData;

          const engagementManagerRecordId = dealData?.engagement_manager?.[0]?.referenced_actor_id;

          if (engagementManagerRecordId) {
            // Use the direct method to get engagement manager details
            return await this.getEngagementManager(engagementManagerRecordId);
          }
        } catch (error) {
          console.warn('Error fetching engagement manager details:', error);
          return {
            name: '-',
            imageSrc: '-',
          };
        }
      }
      // Default values if any step fails
      return {
        name: '-',
        imageSrc: '-',
      };
    } catch (error) {
      console.warn('Error fetching engagement manager details:', error);
      return {
        name: '-',
        imageSrc: '-',
      };
    }
  }

  /**
   * Search for engagement managers by name
   */
  async searchEngagementManagers(searchTerm: string): Promise<AttioEngagementManagerData[]> {
    try {

      const body = {
        filter: {
          name: {
            '$contains': searchTerm,
          },
        },
      };

      const engagementManagersResponse = await this.post<AttioTeamRecordQueryResponse>(this.endpoints.searchTeamMembers(), body);
      return engagementManagersResponse.data.map((engagementManager: AttioTeamRecord) => ({
        name: `${engagementManager.values.name?.[0]?.value || '-'}`,
        imageSrc: `${engagementManager.values.profile_image_url?.[0]?.value || '-'}`,
        targetRecordId: `${engagementManager.id.record_id}`,
      })); 
    } catch (error) {
      console.warn('Error searching engagement managers:', error);
      return [];
    }
  }

  /**
   * Update valuation engagement manager
   */
  async updateValuationEngagementManager({ valuationId, engagementManagerTargetRecordIds }: AttioEngagementManagerUpdateParams): Promise<void> {
    try {
      // Attio PUT overwrites multiselect/relationship values instead of appending (unlike PATCH).
      // A single PUT with the desired value replaces whatever was there before.
      await this.put(this.endpoints.updateValuationByRecordId(valuationId), {
        data: {
          values: {
            engagement_manager: engagementManagerTargetRecordIds.map(id => ({
              target_object: 'team',
              target_record_id: id,
            })),
          },
        },
      });
    } catch (error) {
      console.error('Error updating valuation engagement manager:', error);
      throw error;
    }
  }
  
}

